import { createRoot } from 'react-dom/client';
import { Provider } from 'react-redux';
import QRCode from 'qrcode';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useMemoizedFn, useRequest } from 'ahooks';
import dayjs from 'dayjs';
import { RightOutlined } from '@ant-design/icons';
import utc from 'dayjs/plugin/utc';
import { payUrl } from '@/utils/env';
import { logBDEvent } from '@/utils/logger/bd-tracker';
import { PriceCard } from '@/components/pay_ui/price_card';
import { login } from '@/utils/login';
import store, { useAppSelector } from '@/stores';
import i18n from '@/locales/init';
import { getOrderStatus, getVipOrder } from '@/api/buy';
import { getStudentInfoAsync, fetchUserPoints2 } from '@/stores/slice/user';
import { useDeviceType } from '@/hooks/useDeviceType';
import { BuyQrCode } from '../BuyQrCode';
import { PaySuccess } from '../PaySuccess';
import { PayDialogLayout } from '../PayDialogLayout';
import { PayDrawer } from '../PayDrawer';

const CHECKT_TIMES = 298;

dayjs.extend(utc);

export interface IBuyVipDialogProps {
  onClose?: () => void;
}

export const BuyVipDialog = ({ onClose }: IBuyVipDialogProps) => {
  const [open, setOpen] = useState<boolean>(true);
  const userName = useAppSelector(state => state.user.userName || '雅');
  const avatar = useAppSelector(state => state.user.avatar);
  const isVip = useAppSelector(state => state.user.userPermission.isVip);
  const expireTime = useAppSelector(state => state.user.userPermission.expirationTime);
  const [showList, setShowList] = useState(false);
  const listRef = useRef<HTMLDivElement>(null);

  /**
   * 当前的order，修改时注意清理
   */
  const [currentOrder, setCurrentOrder] = useState<number>(-1);
  /**
   * 所有订单的信息
   */
  const { data: orders, loading, error, run } = useRequest(getVipOrder, {onFinally: (_, data)=>{
    if (currentOrder === -1){
      const isM = data?.data.list.findIndex(v => v.subscript);
      selectOrder(isM ? isM : 0);
    }
  }});

  /**
   * qr code
   */
  const [qrCode, setOrCode] = useState<string>('');
  useEffect(() => {
    const order = orders?.data.list[currentOrder].order_num;
    console.log('********order:', currentOrder, order);
    if (!order) {
      setOrCode('');
      return;
    }
    const targetUrl = `${payUrl}?order=${order}#/pay-redirect.html`;
    console.log('********二维码:', targetUrl);
    QRCode.toDataURL(targetUrl, {
      margin: 0,
    })
      .then((url: string) => {
        setOrCode(url);
      })
      .catch((err: any) => {
        console.error(err);
      });


  }, [orders, currentOrder]);

  /**
   * 轮询当前订单的状态
   */

  const checkOrderStatus = useMemoizedFn(() => {
    const order_num = orders?.data.list[currentOrder].order_num;
    if (order_num) {
      return getOrderStatus(order_num).catch((e) => {
        return Promise.resolve({
          code: 0,
          data: { status: 4 },
        });

      });
    }

    // 错误状态需要关联状态
    return Promise.resolve({
      code: 0,
      data: { status: -1 },
    });
  });

  /**
   * 轮询管理，当前轮询次数
   */
  const [checkTimes, setCheckTimes] = useState<number>(0);
  /**
   * 轮询管理，是否显示刷新按钮
   */
  const [outOfTime, setOutOfTime] = useState<boolean>(false);


  const { loading: checkOrderStatusLoading, data: orderStatus, error: checkOrderStatusError, run: startCheck, cancel: cancelCheck } = useRequest(checkOrderStatus, {
    manual: true,
    pollingInterval: 2000,
    onFinally: () => setCheckTimes(v => v + 1),
  });

  /**
   * 当前订单的状态
   * 0 未知或有错误
   */
  const currentOrderStatus = useMemo(() => {
    return orderStatus?.data?.status || 0;
  }, [orderStatus]);

  useEffect(() => {
    if (checkTimes >= CHECKT_TIMES ) {
      setOutOfTime(true);
      setCheckTimes(0);
      cancelCheck();
      // handleRefreshOrder();
      return;
    }
  }, [checkTimes]);

  useEffect(() => {
    switch (currentOrderStatus) {
      case 1: break;
      case 2:
        // 支付中
        logBDEvent('buy-vip', { type: 'scanning' });
        break;
      case 3:
        // 支付成功
        logBDEvent('buy-vip', { type: 'success', order: currentOrder });
        break;
      case 4:
        // 支付失败
        logBDEvent('buy-vip', { type: 'failed' });
        // handleRefreshOrder();
        break;
      default: break;


    }
  }, [currentOrderStatus]);

  // 更新所有订单
  const handleRefreshOrder = useMemoizedFn(() => {
    run();
    setCheckTimes(0);
    setOutOfTime(false);

  });

  // 修改当前订单
  const selectOrder = useMemoizedFn((index: number) => {
    setCurrentOrder(index);
    cancelCheck();
    startCheck();
  });

  const userInfo = useMemo(() => {
    return {
      userName,
      avatar,
      description: !isVip ? '会员已过期' : i18n.t('会员有效期至{{time}}', { time: dayjs.utc(expireTime * 1000).utc().format('YYYY.MM.DD') }),
    };
  }, [userName, avatar, isVip, expireTime]);

  const priceList: any[] = useMemo(()=>{
    console.log(orders);
    const list = orders?.data.list.map(v=>{
      return {price: v.amount, label: v.desc, dayPrice: v.everyday, isMain: v.subscript};
    }) || [];
    return list;
  },[orders]);

  const handleClose = useMemoizedFn(() => {
    setOpen(false);
    onClose && onClose();
  });

  const order = orders?.data.list[currentOrder];

  useEffect(()=>{
    if (listRef.current){
      const c = listRef.current.children[currentOrder];
      if (c){
        // c.scrollIntoView({behavior: 'smooth', block: 'center'});
        const containerHeight = listRef.current.clientWidth;
        const targetHeight = c.clientWidth;
        const targetTop = (c as HTMLDivElement).offsetLeft;

        const scrollPosition = targetTop - (containerHeight - targetHeight) / 2;
        listRef.current.scrollTo({
          left: scrollPosition,
          behavior: 'smooth'
        });
      }
    }
  },[currentOrder]);

  const handleShowMore = ()=>{
    if (!listRef.current) return;
    let toX = listRef.current.scrollLeft;
    if (showList){
      toX = listRef.current.scrollLeft - 194;
    } else {
      toX = listRef.current.scrollLeft + 194;
    }
    listRef.current.scrollLeft = toX;

  };

  useEffect(() => {
    const handleScroll = () => {
      handleScrollList();
    };

    const element = listRef.current;
    if (element) {
      element.addEventListener('scroll', handleScroll);
    }

    return () => {
      if (element) {
        element.removeEventListener('scroll', handleScroll);
      }
    };
  }, [listRef.current]);

  const handleScrollList = ()=>{
    if (!listRef.current) return;
    const offx = listRef.current ? listRef.current.scrollWidth - listRef.current.clientWidth : 0;
    if (listRef.current.scrollLeft <= 10){
      setShowList(false);
    } else if (listRef.current.scrollLeft >= offx - 10){
      setShowList(true);
    }
  };

  const { is_mobile } = useDeviceType();
  const zoom = (window.innerHeight < 660 || window.innerWidth < 1024) ? 'scale-[0.8]' : 'scale-100';

  const qrContent = <BuyQrCode qrCode={qrCode} currentOrderStatus={currentOrderStatus} outOfTime={outOfTime} loading={false} amount={order?.amount||''}></BuyQrCode>;

  const modalContent = <div className='flex flex-col items-center'>
    {qrContent}
  </div>;

  const goodsContent = (showControl=true)=>{
    return <>
      <div className='relative'>
        <div className='text-black text-[22px] font-bold mb-[10px]'>{i18n.t('购买时长')}</div>
        <div className='flex gap-[14px] pr-[20px] overflow-x-scroll scroll-smooth' ref={listRef}>
          {
            priceList.map((v, i)=><PriceCard key={i} {...v} selected={currentOrder === i} onSelected={
              ()=>{
                selectOrder(i);
              }
            }></PriceCard>)
          }
        </div>
        {
          showControl && <RightOutlined onClick={handleShowMore} className='absolute right-[12px] bottom-[-40px] text-[16px] p-[8px]
            cursor-pointer rounded-full bg-white ' style={{boxShadow: '0 0 5px #0000001A',
            transform: showList ? 'rotate(180deg)' : ''}} />
        }

      </div>

      <div className='text-black text-[22px] font-bold mb-[10px] mt-[10px]'>{i18n.t('题库会员权益')}</div>
      <div className='text-[16px] text-[#4C4C4CCC] min-h-[140px]'>
        <ul className='grid grid-cols-2 leading-[28px] list-disc pl-[18px]'>
          {orders?.data.list[currentOrder]?.illustrate_detail.map((v, i) => <li key={i}>{v.name}</li>)}
        </ul>
      </div>
    </>;
  };

  if (is_mobile) {
    return <PayDrawer
      open={open}
      setOpen={setOpen}
      handleClose={handleClose}
      amount={order?.amount || ''}
      desc={order?.desc || ''}
      userInfo={userInfo}
      modalContent={modalContent}
      content={goodsContent(false)}
      currentOrderStatus={currentOrderStatus}
      orderNumber={order?.order_num || ''}
      successProps={{
        confirm: handleClose,
        confirmText: i18n.t('完成'),
      }}
      refreshOrder={handleRefreshOrder}
    ></PayDrawer>;
  }

  return <PayDialogLayout open={open}
    handleClose={handleClose}
    className={zoom}
    dialogTitle='Buy a Vip'
    content={goodsContent()}
    userInfo={userInfo}
    contentRight={
      <>
        {
          currentOrderStatus === 3 && <PaySuccess confirm={handleClose} />
        }
        {
          currentOrderStatus !== 3 && qrContent
        }
      </>
    }
  ></PayDialogLayout>;
};

let open = false;
export function buyVipDialog(onClose?: () => void) {
  const isLogin = store.getState().user.userPermission.isLogin;
  console.log('=== open');
  if (!isLogin) {
    login();
    return;
  }

  if (open) {
    return;
  }

  logBDEvent('buy-vip', { type: 'open' });
  open = true;
  const container = document.createElement('div');
  container.id = 'buy';
  container.style.position = 'fixed';
  container.className = 'tw_common';
  document.body.appendChild(container);

  const root = createRoot(container);

  const handleClose = () => {
    open = false;
    store.dispatch(getStudentInfoAsync());
    store.dispatch(fetchUserPoints2());
    onClose && onClose();
    root.unmount();
    document.body.removeChild(container);
  };

  root.render(
    <Provider store={store}>
      <BuyVipDialog
        onClose={handleClose}
      />
    </Provider>
  );
}
